import { z } from "zod";

export const OPENPENCIL_FIGMA_SOURCE_BUNDLE_SCHEMA =
  "cogo.studio.openpencil-figma-source-bundle.v2" as const;
export const OPENPENCIL_CLI_READBACK_SCHEMA =
  "openpencil-cli-readback.v2" as const;
export const OPENPENCIL_FACT_MAP_SCHEMA =
  "openpencil-source-fact-map.v2" as const;
export const OPENPENCIL_SURFACE_PREVIEW_MANIFEST_SCHEMA =
  "openpencil-surface-preview-manifest.v1" as const;
export const OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA =
  "cogo.openpencil-bounded-visual-judgement.v1" as const;
export const OPENPENCIL_RUNTIME_PROOF_SCHEMA =
  "cogo.openpencil-runtime-proof-result.v1" as const;
export const OPENPENCIL_FIG_EDIT_PATCH_SCHEMA =
  "cogo.openpencil-fig-edit-patch.v1" as const;
export const OPENPENCIL_FIG_REVISION_EVIDENCE_SCHEMA =
  "cogo.openpencil-fig-revision-evidence.v1" as const;

export const OPENPENCIL_EDITABLE_NODE_TYPES = [
  "CANVAS",
  "FRAME",
  "RECTANGLE",
  "ROUNDED_RECTANGLE",
  "ELLIPSE",
  "TEXT",
  "LINE",
  "STAR",
  "POLYGON",
  "VECTOR",
  "BOOLEAN_OPERATION",
  "GROUP",
  "SECTION",
  "COMPONENT",
  "COMPONENT_SET",
  "INSTANCE",
  "CONNECTOR",
  "SHAPE_WITH_TEXT",
] as const;

export const OPENPENCIL_COMMAND_IDS = [
  "info",
  "pages",
  "sections-query",
  "frame-candidates-query",
  "variables",
  "analyze-colors",
] as const;

export const OPENPENCIL_ANALYSIS_PROFILES = ["full", "ontology"] as const;
export type OpenPencilAnalysisProfile =
  (typeof OPENPENCIL_ANALYSIS_PROFILES)[number];

export const OpenPencilMeasurementStatusSchema = z.enum([
  "observed",
  "skipped",
]);
export type OpenPencilMeasurementStatus = z.infer<
  typeof OpenPencilMeasurementStatusSchema
>;

export const REQUIRED_OPENPENCIL_FACT_KINDS = [
  "source-document-profile",
  "source-page",
  "story-section",
  "surface-candidate",
  "design-token",
  "visual-token-evidence",
] as const;

export const OPENPENCIL_SOURCE_PROFILES = [
  "application-story",
  "design-library",
] as const;
export const OpenPencilSourceProfileSchema = z.enum(
  OPENPENCIL_SOURCE_PROFILES,
);
export type OpenPencilSourceProfile = z.infer<
  typeof OpenPencilSourceProfileSchema
>;

export const REQUIRED_OPENPENCIL_FACT_KINDS_BY_PROFILE = {
  "application-story": REQUIRED_OPENPENCIL_FACT_KINDS,
  "design-library": [
    "source-document-profile",
    "source-page",
    "surface-candidate",
    "design-token",
    "visual-token-evidence",
  ],
} as const;

export const WON_BANKING_OPENPENCIL_CANARY_METRICS = {
  pages: 6,
  totalNodes: 19020,
  sections: 15,
  frameCandidates: 58,
  mobileSizedFrames: 56,
  nonMobileFrames: 2,
  variableCollections: 5,
  variables: 252,
} as const;

const nonEmptyString = z.string().trim().min(1);
const nonNegativeInt = z.number().int().nonnegative();
const confidence = z.number().min(0).max(1);
const boundedCoordinate = z.number().finite().min(-1_000_000).max(1_000_000);
const boundedDimension = z.number().finite().positive().max(1_000_000);
const boundedRotation = z.number().finite().min(-360).max(360);
const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?$/);

export const OPENPENCIL_VARIABLE_TYPES = [
  "COLOR",
  "FLOAT",
  "STRING",
  "BOOLEAN",
] as const;
export const OpenPencilVariableTypeSchema = z.enum(
  OPENPENCIL_VARIABLE_TYPES,
);
export type OpenPencilVariableType = z.infer<
  typeof OpenPencilVariableTypeSchema
>;

export const OpenPencilVariableAliasValueSchema = z
  .object({ aliasId: nonEmptyString.max(500) })
  .strict();
export const OpenPencilVariableModeValueSchema = z.union([
  z.string().max(20_000),
  z.number().finite(),
  z.boolean(),
  OpenPencilVariableAliasValueSchema,
]);
export type OpenPencilVariableModeValue = z.infer<
  typeof OpenPencilVariableModeValueSchema
>;

function isVariableAliasValue(
  value: OpenPencilVariableModeValue,
): value is z.infer<typeof OpenPencilVariableAliasValueSchema> {
  return typeof value === "object";
}

function variableLiteralMatchesType(
  type: OpenPencilVariableType,
  value: OpenPencilVariableModeValue,
): boolean {
  if (isVariableAliasValue(value)) return true;
  switch (type) {
    case "COLOR":
      return typeof value === "string" && hexColor.safeParse(value).success;
    case "FLOAT":
      return typeof value === "number" && Number.isFinite(value);
    case "STRING":
      return typeof value === "string";
    case "BOOLEAN":
      return typeof value === "boolean";
  }
}

const OpenPencilFigEditOperationBaseSchema = z.object({
  nodeId: nonEmptyString.max(500),
  sourceNodeId: nonEmptyString.max(500).optional(),
  expectedNodeType: z.enum(OPENPENCIL_EDITABLE_NODE_TYPES),
  expectedName: z.string().max(500).optional(),
});

export const OpenPencilFigEditOperationSchema = z.union([
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("rename-node"),
    name: nonEmptyString.max(500),
  }).strict(),
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("set-text"),
    text: z.string().max(20_000),
  }).strict(),
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("set-visible"),
    visible: z.boolean(),
  }).strict(),
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("set-opacity"),
    opacity: z.number().finite().min(0).max(1),
  }).strict(),
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("set-solid-fill"),
    fillIndex: nonNegativeInt.max(99),
    expectedColor: hexColor.optional(),
    color: hexColor.optional(),
    fillOpacity: z.number().finite().min(0).max(1).optional(),
  })
    .strict()
    .refine(
      (value) => value.color !== undefined || value.fillOpacity !== undefined,
      { message: "A fill edit must change color or fill opacity." },
    ),
  OpenPencilFigEditOperationBaseSchema.extend({
    operation: z.literal("set-geometry"),
    x: boundedCoordinate.optional(),
    y: boundedCoordinate.optional(),
    width: boundedDimension.optional(),
    height: boundedDimension.optional(),
    rotation: boundedRotation.optional(),
  })
    .strict()
    .refine(
      (value) =>
        value.x !== undefined ||
        value.y !== undefined ||
        value.width !== undefined ||
        value.height !== undefined ||
        value.rotation !== undefined,
      { message: "A geometry edit must change at least one field." },
    ),
  z
    .object({
      operation: z.literal("set-variable-mode-value"),
      variableId: nonEmptyString.max(500),
      collectionId: nonEmptyString.max(500),
      modeId: nonEmptyString.max(500),
      expectedVariableType: OpenPencilVariableTypeSchema,
      expectedValue: OpenPencilVariableModeValueSchema.optional(),
      value: OpenPencilVariableModeValueSchema,
    })
    .strict()
    .superRefine((operation, ctx) => {
      if (
        operation.expectedValue !== undefined &&
        !variableLiteralMatchesType(
          operation.expectedVariableType,
          operation.expectedValue,
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["expectedValue"],
          message: `Expected value must be an alias or a ${operation.expectedVariableType} literal.`,
        });
      }
      if (
        !variableLiteralMatchesType(
          operation.expectedVariableType,
          operation.value,
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: `Value must be an alias or a ${operation.expectedVariableType} literal.`,
        });
      }
    }),
]);
export type OpenPencilFigEditOperation = z.infer<
  typeof OpenPencilFigEditOperationSchema
>;

export const OpenPencilFigEditPatchSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_FIG_EDIT_PATCH_SCHEMA),
  patchId: nonEmptyString.max(160),
  baseSourceId: nonEmptyString.max(160),
  baseSha256: z.string().regex(/^[a-f0-9]{64}$/),
  actor: z.enum(["human", "agent"]),
  reason: nonEmptyString.max(4000),
  operations: z.array(OpenPencilFigEditOperationSchema).min(1).max(100),
  requestedAt: nonEmptyString,
});
export type OpenPencilFigEditPatch = z.infer<
  typeof OpenPencilFigEditPatchSchema
>;

const OpenPencilFigRevisionMetricSchema = z.object({
  pages: nonNegativeInt,
  totalNodes: nonNegativeInt,
  variables: nonNegativeInt,
  variableCollections: nonNegativeInt,
  images: nonNegativeInt,
});
export type OpenPencilFigRevisionMetric = z.infer<
  typeof OpenPencilFigRevisionMetricSchema
>;

const revisionValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const OpenPencilNodeFigRevisionOperationResultSchema = z.object({
  index: nonNegativeInt,
  operation: z.enum([
    "rename-node",
    "set-text",
    "set-visible",
    "set-opacity",
    "set-solid-fill",
    "set-geometry",
  ]),
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.nullable().optional(),
  nodeType: z.enum(OPENPENCIL_EDITABLE_NODE_TYPES),
  before: z.record(revisionValue),
  after: z.record(revisionValue),
});
const OpenPencilVariableFigRevisionOperationResultSchema = z.object({
  index: nonNegativeInt,
  operation: z.literal("set-variable-mode-value"),
  variableId: nonEmptyString,
  collectionId: nonEmptyString,
  modeId: nonEmptyString,
  variableName: nonEmptyString,
  collectionName: nonEmptyString,
  modeName: nonEmptyString,
  variableKey: nonEmptyString.nullable().optional(),
  variableType: OpenPencilVariableTypeSchema,
  before: z.record(revisionValue),
  after: z.record(revisionValue),
});
export const OpenPencilFigRevisionOperationResultSchema = z.union([
  OpenPencilNodeFigRevisionOperationResultSchema,
  OpenPencilVariableFigRevisionOperationResultSchema,
]);
export type OpenPencilFigRevisionOperationResult = z.infer<
  typeof OpenPencilFigRevisionOperationResultSchema
>;

export const OpenPencilFigRevisionEvidenceSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_FIG_REVISION_EVIDENCE_SCHEMA),
  revisionId: nonEmptyString.max(160),
  patchId: nonEmptyString.max(160),
  baseSourceId: nonEmptyString.max(160),
  baseSha256: z.string().regex(/^[a-f0-9]{64}$/),
  outputSha256: z.string().regex(/^[a-f0-9]{64}$/),
  outputSizeBytes: z.number().int().positive(),
  outputFileName: nonEmptyString.regex(/^[^/\\\r\n]+\.fig$/i),
  outputSourceId: nonEmptyString.max(160).optional(),
  outputArtifactRef: nonEmptyString.optional(),
  evidenceRef: nonEmptyString.optional(),
  status: z.literal("applied"),
  operations: z.array(OpenPencilFigRevisionOperationResultSchema).min(1),
  beforeMetrics: OpenPencilFigRevisionMetricSchema,
  afterMetrics: OpenPencilFigRevisionMetricSchema,
  structuralIntegrityPreserved: z.boolean(),
  semanticReadbackVerified: z.literal(true),
  changeIsolationVerified: z.literal(true).optional(),
  parityComparedNodeCount: nonNegativeInt.optional(),
  // Whether change isolation was verified against the writer-canonicalized
  // before-side (the lazy retry) rather than the raw pristine parse. `false` =
  // the raw before-side isolated the edit on the first attempt (fast path, no
  // write+reparse); `true` = the source carried writer normalization on unedited
  // nodes (real customer .fig files) and the canonicalized retry was needed.
  // Additive + optional so prior evidence without the field still validates.
  parityCanonicalizationApplied: z.boolean().optional(),
  // How many bound paints a `set-variable-mode-value` operation re-materialized
  // on reopen. A variable edit is NOT confined to the variable tables on
  // Figma-authored sources: the parser resolves `paint.colorVar` into the
  // materialized colour, so every bound paint moves with the variable. Each one
  // is declared to the change-isolation oracle with its exact expected colour;
  // this count is that declaration, made visible in evidence.
  // Additive + optional so prior evidence without the field still validates.
  variablePaintRematerializationCount: nonNegativeInt.optional(),
  toolVersion: nonEmptyString,
  runnerVersion: nonEmptyString,
  exportedAt: nonEmptyString,
});
export type OpenPencilFigRevisionEvidence = z.infer<
  typeof OpenPencilFigRevisionEvidenceSchema
>;

export const OpenPencilCommandIdSchema = z.enum(OPENPENCIL_COMMAND_IDS);
export type OpenPencilCommandId = z.infer<typeof OpenPencilCommandIdSchema>;

export const OpenPencilCommandEvidenceSchema = z.object({
  commandId: OpenPencilCommandIdSchema,
  command: nonEmptyString,
  status: z.enum(["ok", "failed", "skipped"]),
  durationMs: nonNegativeInt.optional(),
  exitCode: z.number().int().nullable().optional(),
  selector: nonEmptyString.optional(),
  toolVersion: nonEmptyString.optional(),
  runnerVersion: nonEmptyString.optional(),
  message: z.string().max(4000).optional(),
});
export type OpenPencilCommandEvidence = z.infer<
  typeof OpenPencilCommandEvidenceSchema
>;

export const OpenPencilPageFactSchema = z.object({
  kind: z.literal("source-page"),
  id: nonEmptyString,
  name: nonEmptyString,
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.optional(),
  ordinal: nonNegativeInt,
  nodeCount: nonNegativeInt,
  confidence,
});
export type OpenPencilPageFact = z.infer<typeof OpenPencilPageFactSchema>;

export const OpenPencilSectionFactSchema = z.object({
  kind: z.literal("story-section"),
  id: nonEmptyString,
  name: nonEmptyString,
  pageId: nonEmptyString,
  pageName: nonEmptyString,
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.optional(),
  childCount: nonNegativeInt,
  confidence,
});
export type OpenPencilSectionFact = z.infer<typeof OpenPencilSectionFactSchema>;

export const OpenPencilSurfaceCandidateFactSchema = z.object({
  kind: z.literal("surface-candidate"),
  id: nonEmptyString,
  name: nonEmptyString,
  pageId: nonEmptyString,
  pageName: nonEmptyString,
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.optional(),
  width: z.number().positive(),
  height: z.number().positive(),
  classification: z.enum(["mobile", "non-mobile"]),
  sectionId: nonEmptyString.optional(),
  confidence,
});
export type OpenPencilSurfaceCandidateFact = z.infer<
  typeof OpenPencilSurfaceCandidateFactSchema
>;

const OpenPencilVariableModeFactSchema = z.object({
  modeId: nonEmptyString,
  name: nonEmptyString,
  isDefault: z.boolean(),
});

function hasUniqueModeIds(modes: Array<{ modeId: string }>): boolean {
  return new Set(modes.map((mode) => mode.modeId)).size === modes.length;
}

function hasExactlyOneMatchingDefaultMode(
  modes: Array<{ modeId: string; isDefault: boolean }>,
  defaultModeId: string,
): boolean {
  const defaultModes = modes.filter((mode) => mode.isDefault);
  return (
    defaultModes.length === 1 && defaultModes[0]?.modeId === defaultModeId
  );
}

export const OpenPencilVariableCollectionFactSchema = z
  .object({
    id: nonEmptyString,
    name: nonEmptyString,
    defaultModeId: nonEmptyString,
    modes: z.array(OpenPencilVariableModeFactSchema),
    variableCount: nonNegativeInt,
    sourceKey: nonEmptyString.optional(),
    sourceVersion: nonEmptyString.optional(),
  })
  .superRefine((collection, context) => {
    if (!hasUniqueModeIds(collection.modes)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modes"],
        message: "Variable collection mode ids must be unique.",
      });
    }
    if (
      !hasExactlyOneMatchingDefaultMode(
        collection.modes,
        collection.defaultModeId,
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["defaultModeId"],
        message:
          "Variable collection must have exactly one default mode matching defaultModeId.",
      });
    }
  });
export type OpenPencilVariableCollectionFact = z.infer<
  typeof OpenPencilVariableCollectionFactSchema
>;

export const OpenPencilDesignTokenFactSchema = z
  .object({
    kind: z.literal("design-token"),
    id: nonEmptyString,
    name: nonEmptyString,
    collectionId: nonEmptyString,
    collectionName: nonEmptyString,
    tokenType: z.enum([
      "color",
      "typography",
      "spacing",
      "radius",
      "shadow",
      "boolean",
      "string",
      "number",
      "unknown",
    ]),
    value: z.union([z.string(), z.number(), z.boolean()]).optional(),
    defaultModeId: nonEmptyString.optional(),
    modes: z.array(OpenPencilVariableModeFactSchema).default([]),
    valuesByMode: z
      .array(
        z
          .object({
            modeId: nonEmptyString,
            modeName: nonEmptyString,
            value: z.union([z.string(), z.number(), z.boolean()]).optional(),
            aliasId: nonEmptyString.optional(),
            aliasName: nonEmptyString.optional(),
          })
          .refine(
            (entry) => entry.value !== undefined || entry.aliasId !== undefined,
            { message: "A variable mode must preserve a value or alias." },
          ),
      )
      .default([]),
    description: z.string().default(""),
    hiddenFromPublishing: z.boolean().default(false),
    sourceKey: nonEmptyString.optional(),
    sourceVersion: nonEmptyString.optional(),
    sourceLocator: nonEmptyString,
    confidence,
  })
  .superRefine((token, context) => {
    const hasModeEvidence =
      token.defaultModeId !== undefined ||
      token.modes.length > 0 ||
      token.valuesByMode.length > 0;
    if (!hasModeEvidence) return;

    if (!hasUniqueModeIds(token.modes)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modes"],
        message: "Design token mode ids must be unique.",
      });
    }
    if (
      token.defaultModeId === undefined ||
      !hasExactlyOneMatchingDefaultMode(token.modes, token.defaultModeId)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["defaultModeId"],
        message:
          "Design token must have exactly one default mode matching defaultModeId.",
      });
    }

    const valuesByMode = new Map(
      token.valuesByMode.map((entry) => [entry.modeId, entry]),
    );
    const valuesCoverModes =
      valuesByMode.size === token.valuesByMode.length &&
      token.valuesByMode.length === token.modes.length &&
      token.modes.every(
        (mode) => valuesByMode.get(mode.modeId)?.modeName === mode.name,
      );
    if (!valuesCoverModes) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["valuesByMode"],
        message:
          "Design token mode values must cover each declared mode exactly once with the same name.",
      });
    }
  });
export type OpenPencilDesignTokenFact = z.infer<
  typeof OpenPencilDesignTokenFactSchema
>;

export const OpenPencilVisualTokenEvidenceFactSchema = z.object({
  kind: z.literal("visual-token-evidence"),
  id: nonEmptyString,
  tokenId: nonEmptyString.optional(),
  label: nonEmptyString,
  value: nonEmptyString,
  usageCount: nonNegativeInt,
  sourceLocator: nonEmptyString,
  confidence,
});
export type OpenPencilVisualTokenEvidenceFact = z.infer<
  typeof OpenPencilVisualTokenEvidenceFactSchema
>;

export const OpenPencilComponentPropertyDefinitionFactSchema = z.object({
  id: z.string(),
  name: nonEmptyString,
  type: z.enum(["VARIANT", "TEXT", "BOOLEAN", "INSTANCE_SWAP"]),
  defaultValue: z.string(),
  variantOptions: z.array(z.string()).optional(),
});
export type OpenPencilComponentPropertyDefinitionFact = z.infer<
  typeof OpenPencilComponentPropertyDefinitionFactSchema
>;

export const OpenPencilComponentVariantSampleSchema = z.object({
  id: nonEmptyString,
  name: nonEmptyString,
  nodeId: nonEmptyString.optional(),
  sourceNodeId: nonEmptyString.optional(),
  sourceLocator: nonEmptyString.optional(),
  editableSnapshot: z
    .object({
      nodeType: nonEmptyString,
      opacity: z.number().finite().min(0).max(1),
      visible: z.boolean(),
      x: z.number().finite(),
      y: z.number().finite(),
      width: z.number().finite().nonnegative(),
      height: z.number().finite().nonnegative(),
      rotation: z.number().finite(),
    })
    .optional(),
  componentPropertyValues: z.record(z.string()),
  variantPropSpecs: z.array(
    z.object({
      propDefId: z.string(),
      value: z.string(),
    }),
  ),
});
export type OpenPencilComponentVariantSample = z.infer<
  typeof OpenPencilComponentVariantSampleSchema
>;

const OpenPencilResolvedComponentReferenceSchema = z.object({
  mainComponentGraphNodeId: nonEmptyString,
  mainComponentSourceNodeId: nonEmptyString.optional(),
  mainComponentName: nonEmptyString,
  componentSetGraphNodeId: nonEmptyString.optional(),
  componentSetSourceNodeId: nonEmptyString.optional(),
  componentSetName: nonEmptyString.optional(),
});

const OpenPencilComponentReferenceParentVariantSchema = z.object({
  variantGraphNodeId: nonEmptyString,
  variantSourceNodeId: nonEmptyString.optional(),
  variantName: nonEmptyString,
  variantPropertyValues: z.record(z.string()),
  identityStatus: z.enum(["source-stable", "graph-fallback"]),
  observedCount: z.number().int().positive(),
  occurrenceDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
});

export const OpenPencilComponentSetFactSchema = z.object({
  kind: z.literal("component-set"),
  id: nonEmptyString,
  name: nonEmptyString,
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.optional(),
  pageId: nonEmptyString,
  pageName: nonEmptyString,
  componentKey: nonEmptyString.optional(),
  sourceLibraryKey: nonEmptyString.optional(),
  publishId: nonEmptyString.optional(),
  isPublishable: z.boolean(),
  symbolDescription: z.string(),
  symbolLinks: z.array(
    z.object({
      uri: nonEmptyString,
      displayName: z.string().optional(),
      displayText: z.string().optional(),
    }),
  ),
  propertyDefinitions: z.array(OpenPencilComponentPropertyDefinitionFactSchema),
  variantCount: nonNegativeInt,
  observedVariantValues: z.record(z.array(z.string())),
  variantSamples: z.array(OpenPencilComponentVariantSampleSchema),
  variantCoverage: z
    .object({
      strategy: z.literal("property-coverage-greedy-v1"),
      sampleLimit: z.number().int().positive(),
      sampledVariantCount: nonNegativeInt,
      totalCombinationCount: nonNegativeInt,
      duplicateCombinationCount: nonNegativeInt,
      completeValueCoverage: z.boolean(),
      combinationDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
      sampledCombinationDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
      coveredPropertyValues: z.record(z.array(z.string())),
      uncoveredPropertyValues: z.record(z.array(z.string())),
    })
    .optional(),
  slotEvidenceStatus: z.enum(["not-observed", "instance-swap-observed"]),
  slotCandidates: z
    .array(
      z.object({
        propertyDefinitionId: z.string(),
        propertyName: nonEmptyString,
        defaultComponentId: nonEmptyString.optional(),
        semanticStatus: z.literal("unresolved"),
        editableInCogo: z.literal(false),
        sourceLocator: nonEmptyString,
      }),
    )
    .optional(),
  boundVariableIds: z.array(nonEmptyString).default([]),
  anatomyCandidates: z
    .array(
      z.object({
        name: nonEmptyString,
        nodeTypes: z.array(nonEmptyString).min(1),
        observedCount: z.number().int().positive(),
        maxDepth: z.number().int().positive().optional(),
        componentReferenceCoverage: z
          .object({
            instanceObservedCount: z.number().int().positive(),
            resolvedReferenceCount: nonNegativeInt,
            unresolvedReferenceCount: nonNegativeInt,
            sourceStableOccurrenceCount: nonNegativeInt,
            graphFallbackOccurrenceCount: nonNegativeInt,
            resolutionStatus: z.enum([
              "all-resolved",
              "partially-resolved",
              "unresolved",
            ]),
            occurrenceDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
          })
          .optional(),
        componentReferences: z
          .array(
            OpenPencilResolvedComponentReferenceSchema.extend({
              identityStatus: z.enum(["source-stable", "graph-fallback"]),
              observedCount: z.number().int().positive(),
              occurrenceDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
              parentVariants: z
                .array(OpenPencilComponentReferenceParentVariantSchema)
                .min(1),
            }),
          )
          .optional(),
        sampleNodeRefs: z
          .array(
            z.object({
              variantNodeId: nonEmptyString,
              nodeId: nonEmptyString,
              sourceNodeId: nonEmptyString.optional(),
              path: nonEmptyString,
              depth: z.number().int().positive(),
              nodeType: nonEmptyString,
              sourceLocator: nonEmptyString,
              componentRef:
                OpenPencilResolvedComponentReferenceSchema.optional(),
            }),
          )
          .optional(),
      }),
    )
    .default([]),
  anatomyCoverage: z
    .object({
      strategy: z.literal("descendant-preorder-bounded-v1"),
      traversalNodeLimit: z.number().int().positive(),
      traversalDepthLimit: z.number().int().positive(),
      candidateLimit: z.number().int().positive(),
      visitedNodeCount: nonNegativeInt,
      observedCandidateCount: nonNegativeInt,
      emittedCandidateCount: nonNegativeInt,
      traversalTruncated: z.boolean(),
      candidateOutputTruncated: z.boolean(),
    })
    .optional(),
  visualTokenCandidates: z
    .array(
      z.object({
        variableId: nonEmptyString,
        variableName: nonEmptyString,
        value: nonEmptyString,
        usageCount: z.number().int().positive(),
        evidenceKind: z.literal("rendered-color-match"),
        confidence,
      }),
    )
    .default([]),
  sourceLocator: nonEmptyString,
  confidence,
});
export type OpenPencilComponentSetFact = z.infer<
  typeof OpenPencilComponentSetFactSchema
>;

export const OpenPencilComponentInstanceFactSchema = z.object({
  kind: z.literal("component-instance"),
  id: nonEmptyString,
  name: nonEmptyString,
  nodeId: nonEmptyString,
  pageId: nonEmptyString,
  pageName: nonEmptyString,
  parentId: nonEmptyString,
  parentType: nonEmptyString,
  topLevel: z.boolean(),
  componentId: nonEmptyString.optional(),
  componentKey: nonEmptyString.optional(),
  sourceLibraryKey: nonEmptyString.optional(),
  publishId: nonEmptyString.optional(),
  componentPropertyValues: z.record(z.string()),
  overrideKeys: z.array(z.string()),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().nonnegative(),
  height: z.number().finite().nonnegative(),
  opacity: z.number().finite().min(0).max(1),
  visible: z.boolean(),
  sourceLocator: nonEmptyString,
  confidence,
});
export type OpenPencilComponentInstanceFact = z.infer<
  typeof OpenPencilComponentInstanceFactSchema
>;

export const OpenPencilUiOntologyFactsSchema = z.object({
  schemaVersion: z.literal("cogo.openpencil-ui-ontology-facts.v1"),
  counts: z.object({
    componentSets: nonNegativeInt,
    components: nonNegativeInt,
    instances: nonNegativeInt,
  }),
  extraction: z.object({
    extractorVersion: z.enum(["1.0.0", "1.1.0", "1.2.0", "1.3.0"]),
    status: z.literal("ok"),
    durationMs: nonNegativeInt,
    variantSampleLimitPerSet: z.number().int().positive(),
    instanceSampleLimit: z.number().int().positive(),
    variantSamplingStrategy: z
      .literal("property-coverage-greedy-v1")
      .optional(),
    anatomyTraversal: z.literal("descendants").optional(),
  }),
  componentSets: z.array(OpenPencilComponentSetFactSchema),
  componentInstances: z.array(OpenPencilComponentInstanceFactSchema),
});
export type OpenPencilUiOntologyFacts = z.infer<
  typeof OpenPencilUiOntologyFactsSchema
>;

export const OpenPencilSourceDocumentProfileSchema = z.object({
  kind: z.literal("source-document-profile"),
  sourceProfile: OpenPencilSourceProfileSchema.default("application-story"),
  sourceId: nonEmptyString,
  sourceName: nonEmptyString,
  fileName: nonEmptyString,
  sourceArtifactRef: nonEmptyString.optional(),
  pages: nonNegativeInt,
  totalNodes: nonNegativeInt,
  sections: nonNegativeInt,
  frameCandidates: nonNegativeInt,
  mobileSizedFrames: nonNegativeInt,
  nonMobileFrames: nonNegativeInt,
  variableCollections: nonNegativeInt,
  observedVariableCollections: nonNegativeInt,
  variables: nonNegativeInt,
  detectedColors: nonNegativeInt,
  generatedAt: nonEmptyString,
});
export type OpenPencilSourceDocumentProfile = z.infer<
  typeof OpenPencilSourceDocumentProfileSchema
>;

export const OpenPencilTextContentFactSchema = z.object({
  nodeId: nonEmptyString,
  sourceNodeId: nonEmptyString.nullable(),
  pageId: nonEmptyString,
  surfaceId: nonEmptyString.nullable(),
  characters: z.string().min(1),
  styleRunCount: nonNegativeInt,
});
export type OpenPencilTextContentFact = z.infer<
  typeof OpenPencilTextContentFactSchema
>;

export const OpenPencilAnnotationSurfaceFactSchema = z.object({
  surfaceId: nonEmptyString,
  name: nonEmptyString,
  pageId: nonEmptyString,
  linkedScreenSurfaceId: nonEmptyString.nullable(),
  confidence,
  textContent: z.array(OpenPencilTextContentFactSchema),
});
export type OpenPencilAnnotationSurfaceFact = z.infer<
  typeof OpenPencilAnnotationSurfaceFactSchema
>;

export const OpenPencilCliReadbackSchema = z
  .object({
    schemaVersion: z.literal(OPENPENCIL_CLI_READBACK_SCHEMA),
    sourceArtifactRef: nonEmptyString.optional(),
    generatedAt: nonEmptyString,
    analysisProfile: z.enum(OPENPENCIL_ANALYSIS_PROFILES).optional(),
    measurements: z
      .object({
        layout: OpenPencilMeasurementStatusSchema,
        query: OpenPencilMeasurementStatusSchema,
        analyzeColors: OpenPencilMeasurementStatusSchema,
      })
      .optional(),
    info: z.object({
      pages: nonNegativeInt,
      totalNodes: nonNegativeInt,
    }),
    pages: z.object({
      count: nonNegativeInt,
      samples: z.array(OpenPencilPageFactSchema),
    }),
    query: z.object({
      sections: nonNegativeInt,
      frameCandidates: nonNegativeInt,
      mobileSizedFrames: nonNegativeInt,
      nonMobileFrames: nonNegativeInt,
      sectionSamples: z.array(OpenPencilSectionFactSchema),
      surfaceSamples: z.array(OpenPencilSurfaceCandidateFactSchema),
    }),
    variables: z.object({
      declaredCollections: nonNegativeInt,
      observedCollections: nonNegativeInt,
      variables: nonNegativeInt,
      collectionSamples: z
        .array(OpenPencilVariableCollectionFactSchema)
        .optional(),
      samples: z.array(OpenPencilDesignTokenFactSchema),
    }),
    analyzeColors: z.object({
      detectedColors: nonNegativeInt,
      samples: z.array(OpenPencilVisualTokenEvidenceFactSchema),
    }),
    uiOntology: OpenPencilUiOntologyFactsSchema.optional(),
    textContent: z.array(OpenPencilTextContentFactSchema).optional(),
    annotationSurfaces: z
      .array(OpenPencilAnnotationSurfaceFactSchema)
      .optional(),
    performance: z.object({
      loadDocumentMs: nonNegativeInt,
      computeLayoutMs: nonNegativeInt,
      commandExecutionMs: nonNegativeInt,
      totalMs: nonNegativeInt,
    }),
    commandEvidence: z.array(OpenPencilCommandEvidenceSchema),
    rawArtifactRef: nonEmptyString.optional(),
  })
  .superRefine((readback, context) => {
    if (
      readback.analysisProfile === "ontology" &&
      (readback.measurements?.layout !== "skipped" ||
        readback.measurements.query !== "skipped" ||
        readback.measurements.analyzeColors !== "skipped")
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["measurements"],
        message:
          "Ontology-profile readbacks must mark layout, query, and analyzeColors as skipped.",
      });
    }
  });
export type OpenPencilCliReadback = z.infer<typeof OpenPencilCliReadbackSchema>;

export const OpenPencilFactKindSchema = z.enum(REQUIRED_OPENPENCIL_FACT_KINDS);
export type OpenPencilFactKind = z.infer<typeof OpenPencilFactKindSchema>;

export const OpenPencilOntologyFactMapItemSchema = z.object({
  id: nonEmptyString,
  kind: OpenPencilFactKindSchema,
  label: nonEmptyString,
  sourceLocator: nonEmptyString,
  confidence,
  evidence: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export type OpenPencilOntologyFactMapItem = z.infer<
  typeof OpenPencilOntologyFactMapItemSchema
>;

export const OpenPencilSourceFactsSchema = z.object({
  documentProfile: OpenPencilSourceDocumentProfileSchema,
  pages: z.array(OpenPencilPageFactSchema),
  storySections: z.array(OpenPencilSectionFactSchema),
  surfaceCandidates: z.array(OpenPencilSurfaceCandidateFactSchema),
  variableCollections: z
    .array(OpenPencilVariableCollectionFactSchema)
    .optional(),
  designTokens: z.array(OpenPencilDesignTokenFactSchema),
  visualTokenEvidence: z.array(OpenPencilVisualTokenEvidenceFactSchema),
  uiOntology: OpenPencilUiOntologyFactsSchema.optional(),
});
export type OpenPencilSourceFacts = z.infer<typeof OpenPencilSourceFactsSchema>;

export const OpenPencilSurfacePreviewArtifactSchema = z.object({
  surfaceId: nonEmptyString,
  pageId: nonEmptyString,
  nodeId: nonEmptyString,
  fileName: nonEmptyString.regex(/\.png$/i),
  artifactRef: nonEmptyString,
  sourceWidth: z.number().positive(),
  sourceHeight: z.number().positive(),
  renderedWidth: z.number().int().positive(),
  renderedHeight: z.number().int().positive(),
  scale: z.number().positive().max(1),
  contentType: z.literal("image/png"),
  bytes: z.number().int().positive(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type OpenPencilSurfacePreviewArtifact = z.infer<
  typeof OpenPencilSurfacePreviewArtifactSchema
>;

export const OpenPencilSurfacePreviewFailureSchema = z.object({
  surfaceId: nonEmptyString,
  pageId: nonEmptyString,
  nodeId: nonEmptyString,
  message: z.string().trim().min(1).max(4000),
});
export type OpenPencilSurfacePreviewFailure = z.infer<
  typeof OpenPencilSurfacePreviewFailureSchema
>;

export const OpenPencilSurfacePreviewScopeSchema = z.object({
  kind: z.literal("selected-source-node-ids-v1"),
  selectedSourceNodeIds: z
    .array(nonEmptyString)
    .min(1)
    .refine((values) => new Set(values).size === values.length, {
      message: "selected source-node ids must be unique",
    }),
});
export type OpenPencilSurfacePreviewScope = z.infer<
  typeof OpenPencilSurfacePreviewScopeSchema
>;

export const OpenPencilSurfacePreviewManifestSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_SURFACE_PREVIEW_MANIFEST_SCHEMA),
  generatedAt: nonEmptyString,
  scope: OpenPencilSurfacePreviewScopeSchema.optional(),
  requested: nonNegativeInt,
  rendered: nonNegativeInt,
  failed: nonNegativeInt,
  totalBytes: nonNegativeInt,
  items: z.array(OpenPencilSurfacePreviewArtifactSchema),
  failures: z.array(OpenPencilSurfacePreviewFailureSchema),
});
export type OpenPencilSurfacePreviewManifest = z.infer<
  typeof OpenPencilSurfacePreviewManifestSchema
>;

export const OpenPencilVisualAnnotationSchema = z.object({
  surfaceId: nonEmptyString.optional(),
  kind: z.enum([
    "component",
    "icon",
    "text",
    "layout",
    "spacing",
    "token",
    "missing-surface",
    "mismatch",
  ]),
  severity: z.enum(["info", "warning", "blocking"]),
  message: z.string().trim().min(1).max(4000),
  sourceLocator: nonEmptyString.optional(),
});
export type OpenPencilVisualAnnotation = z.infer<
  typeof OpenPencilVisualAnnotationSchema
>;

export const OpenPencilBoundedVisualJudgementSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_VISUAL_JUDGEMENT_SCHEMA),
  judgementId: nonEmptyString,
  judge: z.enum(["gemma", "human"]),
  judgeRole: z.literal("bounded-visual-semantic-judge"),
  model: nonEmptyString.optional(),
  status: z.enum(["approved", "rejected", "blocked"]),
  confidence,
  surfaceIds: z.array(nonEmptyString).min(1),
  annotations: z.array(OpenPencilVisualAnnotationSchema),
  evidenceRefs: z.array(nonEmptyString).min(1),
  reason: z.string().trim().min(1).max(4000).optional(),
  updatedAt: nonEmptyString,
  mayWriteApplicationJson: z.literal(false),
});
export type OpenPencilBoundedVisualJudgement = z.infer<
  typeof OpenPencilBoundedVisualJudgementSchema
>;

export const OpenPencilRuntimeScenarioProofSchema = z.object({
  scenarioId: nonEmptyString,
  status: z.enum(["passed", "failed"]),
  evidenceRefs: z.array(nonEmptyString).min(1),
});
export type OpenPencilRuntimeScenarioProof = z.infer<
  typeof OpenPencilRuntimeScenarioProofSchema
>;

export const OpenPencilRuntimeProofResultSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_RUNTIME_PROOF_SCHEMA),
  proofId: nonEmptyString,
  sourceId: nonEmptyString,
  manifestId: nonEmptyString,
  status: z.enum(["passed", "failed"]),
  runner: nonEmptyString,
  previewUrl: nonEmptyString,
  applicationArtifactRef: nonEmptyString,
  scenarios: z.array(OpenPencilRuntimeScenarioProofSchema).min(1),
  evidenceRefs: z.array(nonEmptyString).min(1),
  canary: z
    .object({
      expected: z.record(nonNegativeInt),
      observed: z.record(nonNegativeInt),
      passed: z.boolean(),
    })
    .optional(),
  blockers: z.array(nonEmptyString),
  createdAt: nonEmptyString,
});
export type OpenPencilRuntimeProofResult = z.infer<
  typeof OpenPencilRuntimeProofResultSchema
>;

export const OpenPencilSidecarRefSchema = z.object({
  fileName: nonEmptyString,
  status: z.enum(["written", "blocked", "pending"]),
  artifactRef: nonEmptyString.optional(),
  bytes: nonNegativeInt.optional(),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
});
export type OpenPencilSidecarRef = z.infer<typeof OpenPencilSidecarRefSchema>;

export const OpenPencilClaimBoundarySchema = z.object({
  accepted: z.array(nonEmptyString).min(1),
  notClaimed: z.array(nonEmptyString).min(1),
  hotPathBans: z.array(nonEmptyString).min(1),
  nextProofs: z.array(nonEmptyString).min(1),
});
export type OpenPencilClaimBoundary = z.infer<
  typeof OpenPencilClaimBoundarySchema
>;

export const OpenPencilSourceGateSchema = z.object({
  id: nonEmptyString,
  scope: z.enum(["source-readback", "generation"]),
  status: z.enum(["pass", "blocked"]),
  message: nonEmptyString,
  evidenceIds: z.array(nonEmptyString),
});
export type OpenPencilSourceGate = z.infer<typeof OpenPencilSourceGateSchema>;

export const OpenPencilGenerationAdmissionSchema = z.object({
  status: z.enum(["blocked", "admitted"]),
  reasonCodes: z.array(nonEmptyString),
  requiredGateIds: z.array(nonEmptyString),
  evaluatedAt: nonEmptyString,
});
export type OpenPencilGenerationAdmission = z.infer<
  typeof OpenPencilGenerationAdmissionSchema
>;

export const OpenPencilBlockedEvidenceSchema = z.object({
  reason: nonEmptyString,
  commandId: OpenPencilCommandIdSchema.optional(),
  exitCode: z.number().int().nullable().optional(),
  stderr: z.string().max(8000).optional(),
  generatedAt: nonEmptyString,
});
export type OpenPencilBlockedEvidence = z.infer<
  typeof OpenPencilBlockedEvidenceSchema
>;

export const OpenPencilSourceRevisionLineageSchema = z.object({
  parentSourceId: nonEmptyString.max(160),
  parentSha256: z.string().regex(/^[a-f0-9]{64}$/),
  sourceRevision: z.number().int().positive(),
  patchId: nonEmptyString.max(160),
  evidenceRef: nonEmptyString,
});
export type OpenPencilSourceRevisionLineage = z.infer<
  typeof OpenPencilSourceRevisionLineageSchema
>;

export const OpenPencilFigmaSourceBundleSchema = z.object({
  schemaVersion: z.literal(OPENPENCIL_FIGMA_SOURCE_BUNDLE_SCHEMA),
  status: z.enum(["ready", "blocked"]),
  sourceProfile: OpenPencilSourceProfileSchema.default("application-story"),
  sourceId: nonEmptyString,
  sourceName: nonEmptyString,
  fileName: nonEmptyString,
  fileSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  sourceArtifactRef: nonEmptyString.optional(),
  workspaceRef: nonEmptyString.optional(),
  revisionLineage: OpenPencilSourceRevisionLineageSchema.optional(),
  generatedAt: nonEmptyString,
  readback: OpenPencilCliReadbackSchema.nullable(),
  factMapSchemaVersion: z.literal(OPENPENCIL_FACT_MAP_SCHEMA),
  factMap: z.array(OpenPencilOntologyFactMapItemSchema),
  sourceFacts: OpenPencilSourceFactsSchema.nullable(),
  visualPreviewManifest: OpenPencilSurfacePreviewManifestSchema.nullable()
    .optional()
    .default(null),
  sidecars: z.record(OpenPencilSidecarRefSchema),
  claimBoundary: OpenPencilClaimBoundarySchema,
  gates: z.array(OpenPencilSourceGateSchema),
  generationAdmission: OpenPencilGenerationAdmissionSchema,
  blockedEvidence: OpenPencilBlockedEvidenceSchema.optional(),
});
export type OpenPencilFigmaSourceBundle = z.infer<
  typeof OpenPencilFigmaSourceBundleSchema
>;

export interface BuildOpenPencilSourceBundleInput {
  sourceProfile?: OpenPencilSourceProfile;
  sourceId?: string;
  sourceName?: string;
  fileName?: string;
  fileSha256?: string;
  sourceArtifactRef?: string;
  workspaceRef?: string;
  revisionLineage?: OpenPencilSourceRevisionLineage;
  generatedAt?: string;
  readback?: unknown;
  visualPreviewManifest?: unknown;
  sidecarRefs?: Partial<Record<string, OpenPencilSidecarRef>>;
}

export interface BuildOpenPencilBlockedInput {
  sourceProfile?: OpenPencilSourceProfile;
  sourceId?: string;
  sourceName?: string;
  fileName?: string;
  fileSha256?: string;
  sourceArtifactRef?: string;
  workspaceRef?: string;
  revisionLineage?: OpenPencilSourceRevisionLineage;
  reason: string;
  commandId?: OpenPencilCommandId;
  exitCode?: number | null;
  stderr?: string;
  generatedAt?: string;
  sidecarRefs?: Partial<Record<string, OpenPencilSidecarRef>>;
}
